import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  connectAuthEmulator,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type Auth,
  type User,
} from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { apiUrl } from './apiBase';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'demo-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'tracttrace-dev.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'tracttrace-dev',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'tracttrace-dev.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '000000000000',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:000000000000:web:demo',
};

/**
 * Opt-in only. Default OFF so the browser never probes loopback ports
 * (Auth :9099 / Firestore :8080), which triggers OS "access other apps" prompts.
 */
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

/**
 * Opt-in live Firestore listeners. Marketing surfaces stay on seed simulation
 * unless explicitly enabled. Live mode requires a non-anonymous ops user with
 * custom claim `{ ops: true }` - anonymous client auth is disabled.
 */
export const useLiveFirestore = import.meta.env.VITE_USE_FIRESTORE_STREAM === 'true';

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const db: Firestore = getFirestore(app);
export const auth: Auth = getAuth(app);

let emulatorsWired = false;
let liveAuthBootstrapped = false;

function wireEmulatorsOnce(): void {
  if (!useEmulators) return;
  if ((globalThis as { __TRACTTRACE_EMULATOR__?: boolean }).__TRACTTRACE_EMULATOR__) return;
  if (emulatorsWired) return;
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  (globalThis as { __TRACTTRACE_EMULATOR__?: boolean }).__TRACTTRACE_EMULATOR__ = true;
  emulatorsWired = true;
}

/**
 * Bootstrap client auth for live Firestore streams.
 * Anonymous sign-in is intentionally removed - pipeline collections require ops claim.
 */
export async function ensureClientAuth(): Promise<void> {
  if (!useLiveFirestore) {
    throw new Error('Live Firestore stream disabled (seed simulation mode)');
  }

  if (liveAuthBootstrapped) return;

  wireEmulatorsOnce();

  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error('Ops sign-in required for live Firestore (anonymous access disabled)');
  }

  const token = await user.getIdTokenResult(/* forceRefresh */ true);
  if (token.claims.ops !== true) {
    throw new Error('Ops claim required for live Firestore reads');
  }

  liveAuthBootstrapped = true;
}

/** Customer portal: email/password signup. */
export async function createCustomerUser(email: string, password: string): Promise<User> {
  wireEmulatorsOnce();
  const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
  return credential.user;
}

/** Customer portal: email/password sign-in. */
export async function signInCustomer(email: string, password: string): Promise<User> {
  wireEmulatorsOnce();
  const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
  return credential.user;
}

export async function signOutCustomer(): Promise<void> {
  await signOut(auth);
}

function firebaseAuthConfigured(): boolean {
  const key = (import.meta.env.VITE_FIREBASE_API_KEY as string | undefined)?.trim() ?? '';
  return key.length > 20 && !key.startsWith('demo');
}

export function watchCustomerAuth(listener: (user: User | null) => void): () => void {
  if (!firebaseAuthConfigured()) {
    listener(null);
    return () => {};
  }
  wireEmulatorsOnce();
  return onAuthStateChanged(auth, listener);
}

export async function getCustomerIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error('Sign in to continue');
  }
  return user.getIdToken(/* forceRefresh */ true);
}

/** Ops console: email/password session (allowlisted server-side via OPS_ADMIN_EMAILS). */
export async function signInOpsAdmin(email: string, password: string): Promise<User> {
  wireEmulatorsOnce();
  liveAuthBootstrapped = false;
  const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
  return credential.user;
}

export async function signOutOpsAdmin(): Promise<void> {
  liveAuthBootstrapped = false;
  await signOut(auth);
}

export function watchOpsAuth(listener: (user: User | null) => void): () => void {
  if (!firebaseAuthConfigured()) {
    listener(null);
    return () => {};
  }
  wireEmulatorsOnce();
  return onAuthStateChanged(auth, listener);
}

/** Bearer token for POST /api/ops/scrape|sweep - never embeds OPS_CRON_SECRET. */
export async function getOpsIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error('Sign in to the ops console before running ingest');
  }
  return user.getIdToken(/* forceRefresh */ true);
}

export async function postOpsIngest(
  path: '/api/ops/scrape' | '/api/ops/sweep',
  body: Record<string, unknown> = {},
): Promise<string> {
  const idToken = await getOpsIdToken();
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body),
  });
  const payload = (await res.json()) as {
    ok: boolean;
    error?: string;
    jobId?: string;
    jobs?: Array<{ jobId: string }>;
  };
  if (!res.ok || !payload.ok) {
    throw new Error(payload.error ?? `Request failed (${res.status})`);
  }
  return payload.jobId ?? payload.jobs?.[0]?.jobId ?? 'ok';
}
